import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Camera, RotateCcw, Check, Zap, ZapOff, FlipHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const CameraCapture = ({ isOpen, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'user' for front, 'environment' for back
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);

  // Check for multiple cameras
  useEffect(() => {
    const checkCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        setHasMultipleCameras(videoDevices.length > 1);
      } catch (err) {
        console.error('Error checking cameras:', err);
      }
    };
    checkCameras();
  }, []);

  // Initialize camera
  const startCamera = useCallback(async () => {
    setIsLoading(true);
    
    // Stop any existing stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      
      setHasPermission(true);
      setIsLoading(false);
    } catch (err) {
      console.error('Camera error:', err);
      setHasPermission(false);
      setIsLoading(false);
      
      if (err.name === 'NotAllowedError') {
        toast.error('Camera access denied', {
          description: 'Please allow camera access in your browser settings'
        });
      } else if (err.name === 'NotFoundError') {
        toast.error('No camera found', {
          description: 'Please connect a camera to use this feature'
        });
      } else {
        toast.error('Camera error', {
          description: 'Unable to access camera. Please try again.'
        });
      }
    }
  }, [facingMode]);

  // Start camera when modal opens
  useEffect(() => {
    if (isOpen) {
      startCamera();
    }
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isOpen, startCamera]);

  // Handle camera switch
  const switchCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const ctx = canvas.getContext('2d');
    
    // Mirror if using front camera
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    
    // Flash effect
    if (flashEnabled) {
      // Create flash overlay effect
      const flashOverlay = document.createElement('div');
      flashOverlay.className = 'camera-flash-overlay';
      document.body.appendChild(flashOverlay);
      setTimeout(() => flashOverlay.remove(), 200);
    }
    
    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Get image data
    const imageData = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageData);
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedImage(null);
  };

  // Confirm and use photo
  const confirmPhoto = () => {
    if (capturedImage) {
      onCapture(capturedImage);
      handleClose();
    }
  };

  // Close modal and cleanup
  const handleClose = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
    setCapturedImage(null);
    setIsLoading(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="camera-modal">
      {/* Header */}
      <div className="camera-header">
        <button onClick={handleClose} className="camera-close-btn">
          <X className="w-6 h-6" />
        </button>
        <span className="camera-title">Take Photo</span>
        <div className="w-10" /> {/* Spacer for centering */}
      </div>

      {/* Camera View */}
      <div className="camera-view">
        {isLoading && (
          <div className="camera-loading">
            <div className="camera-loading-spinner" />
            <p>Starting camera...</p>
          </div>
        )}

        {hasPermission === false && (
          <div className="camera-error">
            <Camera className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">Camera Access Required</p>
            <p className="text-sm opacity-70 mb-4">Please allow camera access to take photos</p>
            <button onClick={startCamera} className="camera-retry-btn">
              Try Again
            </button>
          </div>
        )}

        {/* Live video preview */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`camera-video ${facingMode === 'user' ? 'mirror' : ''} ${capturedImage ? 'hidden' : ''}`}
        />

        {/* Captured image preview */}
        {capturedImage && (
          <img src={capturedImage} alt="Captured" className="camera-preview" />
        )}

        {/* Hidden canvas for capture */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Camera frame guide */}
        {!capturedImage && hasPermission && (
          <div className="camera-frame-guide">
            <div className="camera-frame-corner camera-frame-tl" />
            <div className="camera-frame-corner camera-frame-tr" />
            <div className="camera-frame-corner camera-frame-bl" />
            <div className="camera-frame-corner camera-frame-br" />
            <p className="camera-guide-text">Position clothing in frame</p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="camera-controls">
        {!capturedImage ? (
          // Capture mode controls
          <>
            <button 
              onClick={() => setFlashEnabled(!flashEnabled)} 
              className="camera-control-btn"
              title={flashEnabled ? 'Disable flash' : 'Enable flash'}
            >
              {flashEnabled ? <Zap className="w-6 h-6" /> : <ZapOff className="w-6 h-6" />}
            </button>
            
            <button 
              onClick={capturePhoto} 
              className="camera-capture-btn"
              disabled={!hasPermission || isLoading}
            >
              <div className="camera-capture-inner" />
            </button>
            
            {hasMultipleCameras && (
              <button onClick={switchCamera} className="camera-control-btn" title="Switch camera">
                <FlipHorizontal className="w-6 h-6" />
              </button>
            )}
            {!hasMultipleCameras && <div className="w-12" />}
          </>
        ) : (
          // Preview mode controls
          <>
            <button onClick={retakePhoto} className="camera-action-btn camera-retake">
              <RotateCcw className="w-5 h-5 mr-2" />
              Retake
            </button>
            
            <button onClick={confirmPhoto} className="camera-action-btn camera-confirm">
              <Check className="w-5 h-5 mr-2" />
              Use Photo
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CameraCapture;
